import React, { useEffect, useState } from "react";
import { Stack, Typography, Card } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";

import { FormProvider, RHFTextField, useSnackbar, LocationSelector } from "@app/components";
import { useDialog, useRequestState, useResponsive } from "@app/hooks";
import { useDispatch, useSelector } from "react-redux";
import { beAnInvestor, resetBeAnInvestorState, verifyInvestor } from "../slices";
import {
  selectBeAnInvestorState,
  selectVerifyInvestorState,
} from "../selectors";
import { RequestStatus } from "@app/constants";
import { InvestorSuccessDialog } from "../../../components/modals/investor-success-dialog";
import { Strings } from "../../../constants/strings";
import { selectMyProfileState } from "@app/modules/my-profile/selector";
import { OtpVerificationsDialog } from "@app/modules/auth";
import {
  resendOtpUsingWidget,
  sendOtpUsingWidget,
  verifyOtpUsingWidget,
} from "@app/components/msg91/msg91Otp";

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  state: string;
  city: string;
  zipCode: string;
};

type InvestorVerifyValues = FormValues & {
  otp: string;
  orderId: string;
};

const BeAnInvestor = () => {
  const isMobile = useResponsive("down", "sm");
  const isTablet = useResponsive("down", "md");
  const investorSuccess = useDialog();
  const dispatch = useDispatch();
  const [openOtp, setOpenOtp] = useState(false);
  const [loginFormData, setLoginFormData] = useState<InvestorVerifyValues>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    zipCode: "",
    otp: "",
    orderId: "",
  });

  const handleOtpClose = () => {
    setOpenOtp(false);
    dispatch(resetBeAnInvestorState());
  };

  const { enqueueSnackbar } = useSnackbar();

  const { status, message, requiresOtp, orderId, alreadyExists } = useSelector(
    selectBeAnInvestorState,
  );
  const { status: verifyStatus, message: verifyMessage } = useSelector(
    selectVerifyInvestorState,
  );

  const isLoading = status === RequestStatus.RUNNING;
  const isVerifyLoading = verifyStatus === RequestStatus.RUNNING;

  const { data: profileData } = useRequestState({
    stateSelector: selectMyProfileState,
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      firstName: profileData?.firstName || "",
      lastName: profileData?.lastName || "",
      email: profileData?.email || "",
      phone: profileData?.phone || "",
      state: "",
      city: "",
      zipCode: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset({
      firstName: profileData?.firstName || "",
      lastName: profileData?.lastName || "",
      email: profileData?.email || "",
      phone: profileData?.phone || "",
      state: "",
      city: "",
      zipCode: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const { handleSubmit } = methods;

  const onSubmit = (data: FormValues) => {
    console.log("Be An Investor Form JSON:", JSON.stringify(data, null, 2));
    setLoginFormData({ ...data, otp: "", orderId: "" });
    dispatch(beAnInvestor(data));
  };

  const handleVerifyInvestor = (otp: string) => {
    (async () => {
      try {
        await verifyOtpUsingWidget(otp);
        dispatch(
          verifyInvestor({
            ...loginFormData,
            otp,
            orderId: loginFormData.orderId || orderId || "",
          }),
        );
      } catch (err) {
        enqueueSnackbar("Invalid OTP", { variant: "error" });
      }
    })();
  };

  const handleResendInvestorOtp = () => {
    (async () => {
      try {
        await resendOtpUsingWidget(loginFormData.phone);
        enqueueSnackbar("OTP sent successfully!", { variant: "success" });
      } catch (err) {
        enqueueSnackbar("Unable to resend OTP", { variant: "error" });
      }
    })();
  };

  useEffect(() => {
    if (status === RequestStatus.SUCCESS) {
      if (alreadyExists) {
        enqueueSnackbar(message || "User already exists", {
          variant: "error",
        });
        dispatch(resetBeAnInvestorState());
        return;
      }

      if (requiresOtp) {
        setLoginFormData((prev) => ({ ...prev, orderId: orderId || "" }));
        (async () => {
          try {
            await sendOtpUsingWidget(loginFormData.phone);
            enqueueSnackbar("OTP sent successfully!", { variant: "success" });
            setOpenOtp(true);
          } catch (err) {
            enqueueSnackbar("Unable to send OTP", { variant: "error" });
          }
        })();
        return;
      }

      investorSuccess.show();
    }

    if (status === RequestStatus.ERROR) {
      enqueueSnackbar(message || "Something went wrong", {
        variant: "error",
      });

      dispatch(resetBeAnInvestorState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (verifyStatus === RequestStatus.SUCCESS) {
      setOpenOtp(false);
      investorSuccess.show();
      methods.reset();
      dispatch(resetBeAnInvestorState());
    }

    if (verifyStatus === RequestStatus.ERROR) {
      enqueueSnackbar(verifyMessage || "Something went wrong", {
        variant: "error",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyStatus]);

  return (
    <Stack
      alignItems="center"
      justifyContent={isMobile ? "flex-start" : "center"}
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f6f8",
        px: isMobile ? 2 : 3,
        py: isMobile ? 4 : 6,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : isTablet ? 480 : 520,
          p: isMobile ? 2.5 : 4,
          borderRadius: 3,
          boxShadow: isMobile ? 1 : 3,
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight={600}
          textAlign="center"
          mb={1}
        >
          {Strings.authPopup.beAnInvestor}
        </Typography>

        <Typography
          textAlign="center"
          color="text.secondary"
          mb={isMobile ? 2 : 3}
          fontSize={isMobile ? 13 : 14}
        >
          {Strings.authPopup.joinKaizen}
        </Typography>

        <FormProvider methods={methods}>
          <Stack spacing={isMobile ? 1.5 : 2}>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={isMobile ? 1.5 : 2}
            >
              <RHFTextField
                name="firstName"
                placeholder="First Name"
                rules={{ required: "First name is required" }}
              />
              <RHFTextField
                name="lastName"
                placeholder="Last Name"
                rules={{ required: "Last name is required" }}
              />
            </Stack>

            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={isMobile ? 1.5 : 2}
            >
              <RHFTextField
                name="email"
                placeholder="Email"
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter valid email",
                  },
                }}
              />

              <RHFTextField
                name="phone"
                placeholder="Phone Number"
                rules={{
                  required: "Phone is required",
                }}
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </Stack>

            <LocationSelector variant="default" />

            <LoadingButton
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmit(onSubmit)}
              sx={{
                mt: 1,
                py: isMobile ? 1.3 : 1.6,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: isMobile ? 14 : 16,
              }}
              loading={isLoading}
            >
              {Strings.button.submit}
            </LoadingButton>
          </Stack>
        </FormProvider>
      </Card>
      <InvestorSuccessDialog
        open={investorSuccess.visible}
        onClose={() => {
          investorSuccess.hide();
          dispatch(resetBeAnInvestorState());
        }}
      />
      <OtpVerificationsDialog
        open={openOtp}
        onClose={handleOtpClose}
        loginFormData={loginFormData}
        onVerifyOtp={handleVerifyInvestor}
        onResendOtp={handleResendInvestorOtp}
        isLoading={isVerifyLoading}
      />
    </Stack>
  );
};

export { BeAnInvestor };
